<script>
import { ICONS } from '@modules/atendimento/constants/Text'
import { MODAL } from '@constants/Text'
import ChatReplyPreview from '@modules/atendimento/components/chat-reply-preview/chat-reply-preview.vue'
import ChatMediaUploadPreview from '@modules/atendimento/components/chat-media-upload-preview/chat-media-upload-preview.vue'
import ChatQuickReplies from '@modules/atendimento/components/chat-quick-replies/chat-quick-replies.vue'

export default {
  name: 'ChatInput',
  components: {
    ChatReplyPreview,
    ChatMediaUploadPreview,
    ChatQuickReplies,
  },
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    sending: {
      type: Boolean,
      default: false,
    },
    value: {
      type: String,
      default: '',
    },
    // Seam de store — default atendimento; solicitacoes injeta "solicitacoes".
    namespace: {
      type: String,
      default: 'atendimento',
    },
    // Seam do <input type="file"> — id único por contexto pra evitar colisão
    // de DOM id quando os dois módulos coexistem (ex.: popup do atendimento).
    fileInputId: {
      type: String,
      default: 'atendimento-file-input',
    },
    // Chave do modal de gerenciar respostas rápidas — repassada ao
    // ChatQuickReplies; disjunta por namespace.
    quickRepliesManageKey: {
      type: String,
      default: MODAL.ATENDIMENTO_GERENCIAR_RESPOSTAS,
    },
    // Nota interna podada na V1 do solicitacoes — esconde o toggle do input.
    hideNoteToggle: {
      type: Boolean,
      default: false,
    },
    // Mídia (anexo/colar/preview) podada na V1 da Comunicação — backend de
    // upload ainda não pronto. Mesmo mecanismo do hideNoteToggle: prop
    // contextual; atendimento não passa → default false → mídia ON.
    hideMediaUpload: {
      type: Boolean,
      default: false,
    },
    // Respostas rápidas podadas na V1 da Comunicação — backend é fixture
    // (apiComunicacao.listarRespostasRapidas retorna mock; CRUD é no-op).
    // Esconde o botão + painel + atalho "/"; sem o gatilho, o modal de
    // gerenciar respostas fica inalcançável. Atendimento mantém ON.
    hideQuickReplies: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      icons: ICONS,
      selectedFile: null,
      mediaCaption: '',
      showQuickReplies: false,
      // Incrementado após cada upload/cancelamento pra forçar Vue a
      // re-montar o <input-by-type type="file">. Sem isso, o navegador
      // mantém o value do <input> DOM e o `change` não dispara ao
      // selecionar o mesmo arquivo (ou depois de cancelar + re-tentar).
      fileInputKey: 0,
    }
  },
  computed: {
    replyTo() {
      return this.$store.getters[`${this.namespace}/replyTo`]
    },
    noteMode() {
      return this.$store.getters[`${this.namespace}/noteMode`]
    },
    editingMessage() {
      return this.$store.getters[`${this.namespace}/editingMessage`]
    },
    uploadProgress() {
      return this.$store.getters[`${this.namespace}/uploadProgress`]
    },
    messageText: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      },
    },
    isEditing() {
      return !!this.editingMessage
    },
    hasReply() {
      return !!this.replyTo
    },
    hasFile() {
      return !!this.selectedFile
    },
    isUploading() {
      return this.uploadProgress !== null && this.uploadProgress < 100
    },
    sendDisabled() {
      if (this.disabled) return true
      if (this.isUploading) return true
      if (this.hasFile) return false
      return !this.messageText.trim()
    },
    inputPlaceholder() {
      if (this.noteMode) return 'Escrever nota interna...'
      // Sem respostas rápidas (Comunicação V1) o atalho "/" não existe.
      if (this.hideQuickReplies) return 'Digite uma mensagem...'
      return 'Digite uma mensagem ou / para respostas rápidas...'
    },
  },
  watch: {
    editingMessage(msg) {
      if (msg) {
        this.$emit('input', msg.conteudo || '')
      }
    },
    hasFile(val) {
      if (val) {
        this.$nextTick(() => {
          const preview = this.$refs.mediaPreview
          if (preview && preview.focus) preview.focus()
        })
      }
    },
  },
  methods: {
    onSend() {
      if (this.sendDisabled) return
      this.showQuickReplies = false

      if (this.hasFile) {
        this.$emit('send-media', {
          file: this.selectedFile,
          caption: this.mediaCaption,
          is_note: this.noteMode,
          reply_to_message_id: this.hasReply ? this.replyTo.id : null,
        })
        this.clearFile()
        return
      }

      const text = this.messageText.trim()
      if (!text) return

      const payload = {
        tipo: 'texto',
        conteudo: text,
        is_note: this.noteMode,
      }

      if (this.hasReply) {
        payload.reply_to_message_id = this.replyTo.id
      }

      this.$emit('send', payload)
      this.$emit('input', '')
    },

    onAttachment() {
      const el = document.getElementById(this.fileInputId)
      if (el) el.click()
    },

    onFileSelected(file) {
      if (!file) return
      this.selectedFile = file
      this.mediaCaption = ''
    },

    clearFile() {
      this.selectedFile = null
      this.mediaCaption = ''
      // Força re-mount do <input type="file"> pra limpar o DOM value
      // (evita bug: selecionar o mesmo arquivo ou re-tentar após cancelar
      // não dispara `change` porque o value é idêntico).
      this.fileInputKey += 1
    },

    onCancelReply() {
      this.$store.dispatch(`${this.namespace}/cancelReply`)
    },

    onCancelEdit() {
      this.$store.dispatch(`${this.namespace}/cancelEdit`)
      this.$emit('input', '')
    },

    toggleNoteMode() {
      this.$store.dispatch(`${this.namespace}/setNoteMode`, !this.noteMode)
    },

    focus() {
      if (this.disabled) return
      if (this.hasFile) {
        const preview = this.$refs.mediaPreview
        if (preview && preview.focus) preview.focus()
        return
      }
      const comp = this.$refs.textInput
      if (!comp) return
      // input-by-type renders a v-text-field which wraps a native <input>.
      // We drill into the DOM to focus and move the caret to the end.
      const nativeInput = comp.$el && comp.$el.querySelector('input')
      if (!nativeInput) return
      nativeInput.focus()
      const len = (this.value || '').length
      try {
        nativeInput.setSelectionRange(len, len)
      } catch {
        // best-effort: alguns tipos de <input> (ex.: number) lançam
        // InvalidStateError em setSelectionRange; ignorar é seguro.
      }
    },

    toggleQuickReplies() {
      if (this.hideQuickReplies) return
      this.showQuickReplies = !this.showQuickReplies
    },

    onSelectQuickReply(reply) {
      this.showQuickReplies = false
      this.$emit('input', reply.conteudo || '')
      this.$emit('insert-quick-reply', reply.conteudo || '')
    },

    onCloseQuickReplies() {
      this.showQuickReplies = false
    },

    onKeydown(event) {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault()
        this.onSend()
      }
    },

    // Ctrl+V/Cmd+V de imagem colada → vira anexo (mesma pipeline do botão
    // de clipe). Texto segue colando normalmente (só interceptamos quando
    // há imagem no clipboard). Guardas: nada acontece se o input estiver
    // disabled, se o usuário já tiver outro arquivo selecionado ou se não
    // for uma imagem (ex.: PDF colado vira paste de texto normal).
    onPaste(event) {
      if (this.disabled || this.hasFile || this.hideMediaUpload) return
      const clipboard = event.clipboardData || window.clipboardData
      if (!clipboard || !clipboard.items) return
      let imageFile = null
      for (const item of clipboard.items) {
        if (
          item &&
          item.kind === 'file' &&
          item.type &&
          item.type.startsWith('image/')
        ) {
          const file = item.getAsFile()
          if (file) {
            imageFile = file
            break
          }
        }
      }
      if (!imageFile) return
      // Evita que o path default (paste de texto) dispare — o evento
      // carrega tanto texto quanto imagem em alguns browsers e sem isso
      // veríamos "data:image/..." colado no input junto com o anexo.
      event.preventDefault()
      this.onFileSelected(imageFile)
    },

    onKeyup(event) {
      const val = this.value || ''
      if (val === '/' && !this.hideQuickReplies) {
        this.showQuickReplies = true
      } else if (
        this.showQuickReplies &&
        (event.key === 'Escape' || !val.startsWith('/'))
      ) {
        this.showQuickReplies = false
      }
    },
  },
}
</script>

<template>
  <div
    class="chat-input-wrapper"
    :class="{
      'chat-input-wrapper--note': noteMode,
      'chat-input-wrapper--quick-replies-open': showQuickReplies,
    }"
  >
    <!-- Quick replies popup -->
    <ChatQuickReplies
      v-if="!hideQuickReplies"
      :visible="showQuickReplies"
      :namespace="namespace"
      :manage-modal-key="quickRepliesManageKey"
      @select="onSelectQuickReply"
      @close="onCloseQuickReplies"
    />

    <!-- Reply preview bar -->
    <ChatReplyPreview
      v-if="hasReply"
      :sender-name="replyTo.remetente_nome || replyTo.senderName || ''"
      :content="replyTo.conteudo || ''"
      show-cancel
      @cancel="onCancelReply"
    />

    <!-- Media upload preview -->
    <ChatMediaUploadPreview
      v-if="hasFile && !hideMediaUpload"
      ref="mediaPreview"
      :file="selectedFile"
      :progress="uploadProgress"
      :caption="mediaCaption"
      @cancel="clearFile"
      @update:caption="mediaCaption = $event"
      @send="onSend"
    />

    <!-- Note mode banner -->
    <div
      v-if="noteMode"
      data-test="note-mode-banner"
      class="note-mode-banner d-flex align-center pa-2"
    >
      <icon-svg
        :name="icons.lockOutline"
        size="16"
        color="amber darken-3"
        class="mr-2"
      />
      <span class="az-text-small flex-grow-1">
        <strong>Nota interna</strong> — só o time vê, não vai pro WhatsApp do
        cliente.
      </span>
      <v-btn
        icon
        small
        aria-label="Fechar modo nota interna"
        @click="toggleNoteMode"
      >
        <icon-svg :name="icons.close" size="12" color="#8E8E8E" />
      </v-btn>
    </div>

    <!-- Input bar -->
    <div class="chat-input d-flex align-center px-4 py-3">
      <!-- Note toggle: alterna entre mensagem WhatsApp e nota interna.
           Podado na V1 do solicitacoes via hideNoteToggle. -->
      <v-tooltip v-if="!hideNoteToggle" top>
        <template v-slot:activator="{ on }">
          <v-btn
            icon
            small
            data-test="note-toggle"
            class="mr-1"
            :class="{ 'note-active': noteMode }"
            :disabled="disabled"
            :aria-label="
              noteMode
                ? 'Voltar para mensagem do WhatsApp'
                : 'Escrever nota interna'
            "
            :aria-pressed="noteMode ? 'true' : 'false'"
            v-on="on"
            @click="toggleNoteMode"
          >
            <icon-svg
              :name="
                noteMode ? 'mdi-note-edit-outline' : 'mdi-note-plus-outline'
              "
              size="20"
              :color="noteMode ? 'amber darken-3' : undefined"
            />
          </v-btn>
        </template>
        <span>{{
          noteMode
            ? 'Voltar para mensagem do WhatsApp'
            : 'Escrever nota interna'
        }}</span>
      </v-tooltip>

      <!-- Attachment button -->
      <v-tooltip v-if="!hideMediaUpload" top>
        <template v-slot:activator="{ on }">
          <v-btn
            icon
            small
            data-test="attachment-button"
            class="mr-1"
            :disabled="disabled"
            aria-label="Anexar arquivo"
            v-on="on"
            @click="onAttachment"
          >
            <icon-svg :name="icons.attachment" size="20" />
          </v-btn>
        </template>
        <span>Anexar arquivo</span>
      </v-tooltip>

      <!-- Quick replies button -->
      <v-tooltip v-if="!hideQuickReplies" top>
        <template v-slot:activator="{ on }">
          <v-btn
            icon
            small
            data-test="quick-reply-btn"
            class="mr-2"
            :class="{ 'quick-reply-active': showQuickReplies }"
            :disabled="disabled"
            aria-label="Respostas rápidas"
            :aria-expanded="showQuickReplies ? 'true' : 'false'"
            v-on="on"
            @click="toggleQuickReplies"
          >
            <icon-svg
              :name="icons.quickReplies"
              size="20"
              :color="showQuickReplies ? 'primary' : undefined"
            />
          </v-btn>
        </template>
        <span>Respostas rápidas</span>
      </v-tooltip>

      <!-- Hidden file input (input-by-type for design-system compliance) -->
      <input-by-type
        v-if="!hideMediaUpload"
        :key="fileInputId + '-' + fileInputKey"
        type="file"
        :id-input="fileInputId"
        class="d-none"
        @add-file="onFileSelected"
      />

      <!-- Text input — uses input-by-type with compact/pill visual override -->
      <input-by-type
        ref="textInput"
        v-model="messageText"
        type="text"
        data-test="message-input"
        :placeholder="inputPlaceholder"
        aria-label="Digite uma mensagem"
        class="chat-input__field flex-grow-1"
        :disabled="disabled || hasFile"
        hide-details
        outlined
        single-line
        @keydown.native="onKeydown"
        @keyup.native="onKeyup"
        @paste.native="onPaste"
        @focus.native="$emit('focus')"
      />

      <!-- Send button -->
      <v-btn
        icon
        small
        data-test="send-button"
        :disabled="sendDisabled || sending"
        :aria-label="sending ? 'Enviando mensagem' : 'Enviar mensagem'"
        :aria-busy="sending ? 'true' : 'false'"
        class="ml-2"
        @click="onSend"
      >
        <v-progress-circular
          v-if="sending"
          indeterminate
          size="20"
          width="2"
          color="primary"
        />
        <icon-svg
          v-else
          :name="icons.send"
          size="20"
          :color="sendDisabled ? 'grey' : 'primary'"
        />
      </v-btn>
    </div>
  </div>
</template>

<style lang="sass" scoped>
// Tokens locais do chat-input — mantidos como SASS vars por ora porque não
// há equivalente direto em :root (CSS vars). Mesma estratégia aplicada em
// chat-message.vue:508-517 (ver rationale lá). Quando o design-system
// expor um token amber/info genérico, migrar para var(--*).
$chat-input-border: #e0e0e0              // border-top divisor input bar
$chat-input-note-border: #FFA000         // stripe amber do modo nota (left)
$chat-input-note-bg: #FFF8E1             // bg amber claro — modo nota ativo + banner
$chat-input-note-border-banner: #FFE082  // bordas do banner de nota
$chat-input-note-text: #F57F17           // texto do banner de nota
$chat-input-quick-reply-bg: #E3F2FD      // bg blue claro — quick-reply ativo

.chat-input-wrapper
  background-color: var(--panel-bg)
  position: relative

.chat-input-wrapper--note
  border-left: 2px solid $chat-input-note-border

.chat-input
  background-color: var(--panel-bg)
  border-top: 1px solid $chat-input-border

// Quando o popup de respostas rápidas está aberto, ele encosta no topo
// do input-bar. Remove essa border-top pra não criar uma linha divisória
// visível entre o popup e o input (eles ficam conectados visualmente).
.chat-input-wrapper--quick-replies-open .chat-input
  border-top: none

.note-active
  background-color: $chat-input-note-bg !important

.quick-reply-active
  background-color: $chat-input-quick-reply-bg !important

.note-mode-banner
  background-color: $chat-input-note-bg
  border-top: 1px solid $chat-input-note-border-banner
  border-bottom: 1px solid $chat-input-note-border-banner
  color: $chat-input-note-text
</style>
